// Iter 82.9 Sprint 2 — Payroll Manual Inputs panel.
//
// 4 categories per operator spec:
//   • Variable Bonus     → buckets into payroll_run.manual_lines[].variable_bonus
//   • Reimbursement      → reimbursements
//   • One-time Adjustment → manual_adjustments[] with kind="earning"
//   • Recovery           → manual_adjustments[] with kind="deduction"
//
// 2 modes:
//   • CSV upload (employee_code,label,amount)
//   • Single-employee line entry
//
// Saves the full per-employee payload via the existing
// PUT /api/payroll/run/{rid}/manual-inputs endpoint (no new backend).
import { useCallback, useEffect, useState, useRef } from "react";
import {
    Plus, Trash, Upload, Download, MagnifyingGlass, FloppyDisk, ShieldCheck,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Operator-visible category → server bucket mapping
const CATEGORIES = [
    { key: "variable_bonus", title: "Variable Bonus", kind: null, fmt: "VB" },
    { key: "reimbursements", title: "Reimbursement", kind: null, fmt: "RE" },
    { key: "adjustment_earning", title: "One-time Adjustment (positive)", kind: "earning", fmt: "AJ+", serverBucket: "manual_adjustments" },
    { key: "adjustment_deduction", title: "Recovery (deduction)", kind: "deduction", fmt: "RC-", serverBucket: "manual_adjustments" },
];

function emptyForm() {
    return { employee_id: "", label: "", amount: "" };
}

function parseCsv(text) {
    // employee_code,label,amount per line. First line may be a header.
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    const start = /employee_code|employee/i.test(lines[0]) ? 1 : 0;
    const out = [];
    for (let i = start; i < lines.length; i++) {
        const parts = lines[i].split(",").map((p) => p.trim());
        if (parts.length < 3) continue;
        const [employee_code, label, amount] = parts;
        if (!employee_code || isNaN(Number(amount))) continue;
        out.push({ employee_code, label, amount: Number(amount) });
    }
    return out;
}

export default function PayrollManualInputsPanel({ runId, run, canEdit, onSaved }) {
    const [employees, setEmployees] = useState([]);
    const [byEmp, setByEmp] = useState({}); // employee_id → { variable_bonus, reimbursements, manual_adjustments }
    const [empSearch, setEmpSearch] = useState("");
    const [activeCat, setActiveCat] = useState("variable_bonus");
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    // Phase E3.2 Sprint 2 — Validated Bulk Import (D8 partial-commit, D9 ≤2000).
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkReport, setBulkReport] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [pendingRows, setPendingRows] = useState([]);  // rows submitted for validation
    const fileInputRef = useRef(null);
    const bulkFileInputRef = useRef(null);

    const isFrozen = !(run?.status === "Draft" || run?.status === "Calculated");

    const reload = useCallback(async () => {
        // Existing manual lines from the run + employee list for picker
        const next = {};
        for (const ml of (run?.manual_lines || [])) {
            next[ml.employee_id] = {
                variable_bonus: ml.variable_bonus || [],
                reimbursements: ml.reimbursements || [],
                manual_adjustments: ml.manual_adjustments || [],
            };
        }
        setByEmp(next);
        try {
            const e = await api.get("/hr/employee/list", { params: { page_size: 200, status_filter: "Active" } });
            setEmployees(e.data.items || []);
        } catch (_) {
            setEmployees([]);
        }
    }, [run]);

    useEffect(() => { reload(); }, [reload]);

    const empByCode = Object.fromEntries(employees.map((e) => [e.employee_code, e]));
    const empById = Object.fromEntries(employees.map((e) => [e.id, e]));

    const filteredEmployees = employees.filter((e) => {
        if (!empSearch) return true;
        const q = empSearch.toLowerCase();
        return (e.full_name || "").toLowerCase().includes(q)
            || (e.employee_code || "").toLowerCase().includes(q)
            || (e.department || "").toLowerCase().includes(q);
    });

    const addRow = (employee_id, catKey, label, amount, kind) => {
        const cat = CATEGORIES.find((c) => c.key === catKey);
        if (!cat) return;
        const entry = byEmp[employee_id] || { variable_bonus: [], reimbursements: [], manual_adjustments: [] };
        const bucket = cat.serverBucket || cat.key;
        const row = bucket === "manual_adjustments"
            ? { label, amount: Number(amount), kind: cat.kind }
            : { label, amount: Number(amount) };
        const next = { ...entry, [bucket]: [...(entry[bucket] || []), row] };
        setByEmp({ ...byEmp, [employee_id]: next });
    };

    const removeRow = (employee_id, bucket, idx) => {
        const entry = byEmp[employee_id];
        if (!entry) return;
        const next = { ...entry, [bucket]: entry[bucket].filter((_, i) => i !== idx) };
        setByEmp({ ...byEmp, [employee_id]: next });
    };

    const onSingleEntrySubmit = (e) => {
        e.preventDefault();
        if (!form.employee_id || !form.label || !form.amount) {
            toast.error("Pick an employee, fill label + amount");
            return;
        }
        addRow(form.employee_id, activeCat, form.label, form.amount);
        setForm(emptyForm());
        toast.success("Added — don't forget to Save");
    };

    const onCsvFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const text = await f.text();
        const parsed = parseCsv(text);
        let added = 0, skipped = 0;
        for (const r of parsed) {
            const emp = empByCode[r.employee_code];
            if (!emp) { skipped += 1; continue; }
            addRow(emp.id, activeCat, r.label || activeCat, r.amount);
            added += 1;
        }
        toast.success(`CSV ingested — ${added} added, ${skipped} skipped (unknown employee_code)`);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = Object.entries(byEmp)
                .filter(([, v]) => (
                    (v.variable_bonus?.length || 0)
                    + (v.reimbursements?.length || 0)
                    + (v.manual_adjustments?.length || 0)
                ) > 0)
                .map(([employee_id, v]) => ({
                    employee_id,
                    variable_bonus: v.variable_bonus || [],
                    reimbursements: v.reimbursements || [],
                    manual_adjustments: v.manual_adjustments || [],
                }));
            await api.put(`/payroll/run/${runId}/manual-inputs`, payload);
            toast.success(`Saved manual inputs for ${payload.length} employees`);
            onSaved && onSaved();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const csvTemplate = `employee_code,label,amount
EMP-0001,Performance Bonus Q1,25000
EMP-0002,Travel Reimbursement,8500`;

    const downloadTemplate = () => {
        const blob = new Blob([csvTemplate], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `manual_inputs_template_${activeCat}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── Phase E3.2 Sprint 2 — Validated Bulk Import ────────────────────
    // Parses a CSV with columns:
    //   employee_code, category, label, amount[, currency]
    // Posts to /validate, shows per-row outcome, then operator confirms import.
    const parseBulkCsv = (text) => {
        const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        if (lines.length === 0) return [];
        const start = /employee_code/i.test(lines[0]) ? 1 : 0;
        const rows = [];
        for (let i = start; i < lines.length; i++) {
            const parts = lines[i].split(",").map((p) => p.trim());
            if (parts.length < 4) continue;
            const [employee_code, category, label, amount, currency] = parts;
            rows.push({
                employee_code, category: category || "variable_bonus", label,
                amount: Number(amount) || 0, currency: currency || "INR",
            });
        }
        return rows;
    };

    const downloadBulkTemplate = () => {
        const sample = `employee_code,category,label,amount,currency
EMP-0001,variable_bonus,Performance Bonus Q1,25000,INR
EMP-0001,reimbursement,Internet Reimbursement,1500,INR
EMP-0002,manual_adjustment,Festival Bonus,5000,INR
EMP-0003,recovery,Salary Advance Recovery,3000,INR`;
        const blob = new Blob([sample], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "bulk_import_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const onBulkCsvFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const text = await f.text();
        const rows = parseBulkCsv(text);
        if (rows.length === 0) {
            toast.error("CSV is empty or invalid format. Expected: employee_code,category,label,amount[,currency]");
            return;
        }
        if (rows.length > 2000) {
            toast.error(`Row cap exceeded: ${rows.length} > 2000. Split the file and retry.`);
            return;
        }
        setBulkBusy(true);
        setPendingRows(rows);
        try {
            const r = await api.post(`/payroll/run/${runId}/manual-inputs/validate`, { rows });
            setBulkReport(r.data);
            setBulkOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Validation failed"));
        } finally {
            setBulkBusy(false);
            if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
        }
    };

    const onBulkImportConfirm = async () => {
        if (!bulkReport) return;
        setBulkBusy(true);
        try {
            const r = await api.post(`/payroll/run/${runId}/manual-inputs/import`, { rows: pendingRows });
            const persisted = r.data.persisted_count;
            const rejected = r.data.summary.rejected;
            toast.success(`Imported ${persisted} row(s) · Rejected ${rejected}`);
            setBulkOpen(false);
            setBulkReport(null);
            setPendingRows([]);
            onSaved && onSaved();
        } catch (err) {
            toast.error(formatApiError(err, "Import failed"));
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div className="space-y-4 pt-3" data-testid="manual-inputs-panel">
            {isFrozen && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded" data-testid="manual-frozen-notice">
                    Run is in <strong>{run?.status}</strong> — manual inputs are locked.
                    Revert to Draft (or use Recalculate) to edit.
                </div>
            )}
            <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col">
                    <Label className="text-xs mb-1">Category</Label>
                    <select
                        value={activeCat}
                        onChange={(e) => setActiveCat(e.target.value)}
                        className="h-9 px-3 border rounded-md text-sm"
                        data-testid="manual-category-select"
                        disabled={!canEdit || isFrozen}
                    >
                        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
                    </select>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="manual-csv-template">
                    <Download size={14} className="mr-1" /> CSV Template
                </Button>
                <label className="inline-flex">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={onCsvFile}
                        className="hidden"
                        disabled={!canEdit || isFrozen}
                        data-testid="manual-csv-file"
                    />
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={!canEdit || isFrozen} data-testid="manual-csv-upload">
                        <Upload size={14} className="mr-1" /> Upload CSV
                    </Button>
                </label>
                {/* Iter 82.12 / Sprint 2 — Validated Bulk Import */}
                <Button variant="outline" size="sm" onClick={downloadBulkTemplate} data-testid="bulk-import-template">
                    <Download size={14} className="mr-1" /> Bulk Template
                </Button>
                <label className="inline-flex">
                    <input
                        ref={bulkFileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={onBulkCsvFile}
                        className="hidden"
                        disabled={!canEdit || isFrozen}
                        data-testid="bulk-import-file"
                    />
                    <Button variant="outline" size="sm" onClick={() => bulkFileInputRef.current?.click()} disabled={!canEdit || isFrozen || bulkBusy} data-testid="bulk-import-validate-btn">
                        <ShieldCheck size={14} className="mr-1" />
                        {bulkBusy ? "Validating…" : "Validated Bulk Import"}
                    </Button>
                </label>
                <span className="flex-1" />
                {canEdit && !isFrozen && (
                    <Button size="sm" onClick={onSave} disabled={saving} data-testid="manual-save-btn">
                        <FloppyDisk size={14} className="mr-1" /> Save All
                    </Button>
                )}
            </div>

            <form onSubmit={onSingleEntrySubmit} className="border rounded-lg bg-white p-3 space-y-2" data-testid="manual-single-form">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Add single-employee entry</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div className="md:col-span-2">
                        <Label className="text-xs">Employee</Label>
                        <select
                            value={form.employee_id}
                            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                            className="h-9 px-2 w-full border rounded-md text-sm"
                            disabled={!canEdit || isFrozen}
                            data-testid="manual-form-emp"
                        >
                            <option value="">— Pick employee —</option>
                            {employees.map((e) => (
                                <option key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Label</Label>
                        <Input
                            value={form.label}
                            onChange={(e) => setForm({ ...form, label: e.target.value })}
                            placeholder="e.g. Q1 Performance Bonus"
                            disabled={!canEdit || isFrozen}
                            data-testid="manual-form-label"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Amount (₹)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            placeholder="0"
                            disabled={!canEdit || isFrozen}
                            data-testid="manual-form-amount"
                        />
                    </div>
                </div>
                <Button type="submit" size="sm" variant="outline" disabled={!canEdit || isFrozen} data-testid="manual-form-add">
                    <Plus size={14} className="mr-1" /> Add to {CATEGORIES.find((c) => c.key === activeCat)?.title}
                </Button>
            </form>

            <div className="space-y-2" data-testid="manual-byemp-list">
                {Object.entries(byEmp).filter(([, v]) => (
                    (v.variable_bonus?.length || 0) + (v.reimbursements?.length || 0) + (v.manual_adjustments?.length || 0) > 0
                )).map(([empId, v]) => {
                    const emp = empById[empId] || { full_name: empId };
                    if (empSearch && !((emp.full_name || "").toLowerCase().includes(empSearch.toLowerCase())
                                       || (emp.employee_code || "").toLowerCase().includes(empSearch.toLowerCase()))) {
                        return null;
                    }
                    return (
                        <article key={empId} className="border rounded-lg bg-white p-3" data-testid={`manual-emp-${emp.employee_code || empId}`}>
                            <header className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-slate-800 text-sm">{emp.full_name}</div>
                                    <div className="text-[10px] font-mono text-slate-400">{emp.employee_code}</div>
                                </div>
                            </header>
                            <div className="mt-2 space-y-1 text-sm">
                                {(v.variable_bonus || []).map((r, i) => (
                                    <div key={`vb${i}`} className="flex items-center gap-2 text-xs">
                                        <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200">VB</Badge>
                                        <span className="flex-1">{r.label}</span>
                                        <span className="font-mono">{inr(r.amount)}</span>
                                        {canEdit && !isFrozen && <Button variant="ghost" size="sm" onClick={() => removeRow(empId, "variable_bonus", i)}><Trash size={12} /></Button>}
                                    </div>
                                ))}
                                {(v.reimbursements || []).map((r, i) => (
                                    <div key={`re${i}`} className="flex items-center gap-2 text-xs">
                                        <Badge className="bg-sky-50 text-sky-800 border-sky-200">RE</Badge>
                                        <span className="flex-1">{r.label}</span>
                                        <span className="font-mono">{inr(r.amount)}</span>
                                        {canEdit && !isFrozen && <Button variant="ghost" size="sm" onClick={() => removeRow(empId, "reimbursements", i)}><Trash size={12} /></Button>}
                                    </div>
                                ))}
                                {(v.manual_adjustments || []).map((r, i) => (
                                    <div key={`aj${i}`} className="flex items-center gap-2 text-xs">
                                        <Badge className={r.kind === "deduction" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}>
                                            {r.kind === "deduction" ? "RC-" : "AJ+"}
                                        </Badge>
                                        <span className="flex-1">{r.label}</span>
                                        <span className="font-mono">{inr(r.amount)}</span>
                                        {canEdit && !isFrozen && <Button variant="ghost" size="sm" onClick={() => removeRow(empId, "manual_adjustments", i)}><Trash size={12} /></Button>}
                                    </div>
                                ))}
                            </div>
                        </article>
                    );
                })}
                {Object.keys(byEmp).length === 0 && (
                    <div className="text-center text-sm text-slate-400 py-6" data-testid="manual-empty">
                        No manual inputs yet. Add a single entry above, or upload a CSV.
                    </div>
                )}
            </div>

            {/* Iter 82.12 / Sprint 2 — Validated Bulk Import dialog */}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="max-w-3xl" data-testid="bulk-import-dialog">
                    <DialogHeader>
                        <DialogTitle>Bulk Import Validation Report</DialogTitle>
                        <DialogDescription>
                            Review the per-row outcome below. Click <strong>Confirm Import</strong> to persist
                            ONLY the accepted rows; rejected rows are skipped. Partial-commit
                            semantics (D8) — fewest restarts.
                        </DialogDescription>
                    </DialogHeader>
                    {bulkReport && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-4 gap-2 text-center">
                                <div className="border rounded p-2 bg-slate-50" data-testid="bulk-summary-total">
                                    <div className="text-[10px] uppercase text-slate-500">Total</div>
                                    <div className="text-xl font-mono font-semibold">{bulkReport.summary?.total || 0}</div>
                                </div>
                                <div className="border rounded p-2 bg-emerald-50" data-testid="bulk-summary-accepted">
                                    <div className="text-[10px] uppercase text-emerald-700">Accepted</div>
                                    <div className="text-xl font-mono font-semibold text-emerald-800">{bulkReport.summary?.accepted || 0}</div>
                                </div>
                                <div className="border rounded p-2 bg-rose-50" data-testid="bulk-summary-rejected">
                                    <div className="text-[10px] uppercase text-rose-700">Rejected</div>
                                    <div className="text-xl font-mono font-semibold text-rose-800">{bulkReport.summary?.rejected || 0}</div>
                                </div>
                                <div className="border rounded p-2 bg-slate-50" data-testid="bulk-summary-cohort">
                                    <div className="text-[10px] uppercase text-slate-500">Cohort Size</div>
                                    <div className="text-xl font-mono font-semibold">{bulkReport.cohort_size || 0}</div>
                                </div>
                            </div>
                            <div className="max-h-80 overflow-y-auto border rounded bg-white" data-testid="bulk-outcomes-table">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                                        <tr>
                                            <th className="px-2 py-1 text-left">Row</th>
                                            <th className="px-2 py-1 text-left">Outcome</th>
                                            <th className="px-2 py-1 text-left">Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {(bulkReport.outcomes || []).slice(0, 200).map((o) => (
                                            <tr key={o.idx} className="hover:bg-slate-50" data-testid={`bulk-outcome-${o.idx}`}>
                                                <td className="px-2 py-1 font-mono">{o.idx + 1}</td>
                                                <td className="px-2 py-1">
                                                    {o.outcome === "accept" ? (
                                                        <Badge className="bg-emerald-50 text-emerald-800 text-[10px]">accept</Badge>
                                                    ) : (
                                                        <Badge className="bg-rose-50 text-rose-800 text-[10px]">{o.outcome}</Badge>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1 text-slate-600">{o.reason || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {bulkReport.outcomes?.length > 200 && (
                                    <div className="text-[10px] text-slate-400 text-center py-1">
                                        Showing first 200 of {bulkReport.outcomes.length} rows.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkReport(null); setPendingRows([]); }}>
                            Cancel
                        </Button>
                        <Button onClick={onBulkImportConfirm} disabled={bulkBusy || !bulkReport || bulkReport.summary?.accepted === 0}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="bulk-import-confirm-btn">
                            {bulkBusy ? "Importing…" : `Confirm Import (${bulkReport?.summary?.accepted || 0} rows)`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
